/**
 * Console session. Stores the OTP-verified token (issued by /otp/verify),
 * the phone we authenticated as, and which console the person chose at the
 * landing chooser (`vendor` or `user`). Persisted to localStorage so a page
 * refresh doesn't kick the tester out mid-flow.
 *
 * NOTE: the token is short-lived (1 hour). On expiry, protected calls 401 and
 * the router bounces back to the role's login screen.
 *
 * The token field is still named `onboarding_token` because that's literally
 * what the OTP verify endpoint returns for both roles — the same OTP-verified
 * JWT gates vendor onboarding and user (pet-owner) APIs alike.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type SessionRole = 'vendor' | 'user';

type SessionState = {
  onboarding_token: string | null;
  phone: string | null;
  role: SessionRole | null;
  issued_at: number | null;
  set_session: (s: { onboarding_token: string; phone: string; role: SessionRole }) => void;
  clear: () => void;
};

export const use_session_store = create<SessionState>()(
  persist(
    (set) => ({
      onboarding_token: null,
      phone: null,
      role: null,
      issued_at: null,
      set_session: (s) =>
        set({
          onboarding_token: s.onboarding_token,
          phone: s.phone,
          role: s.role,
          issued_at: Date.now(),
        }),
      clear: () =>
        set({ onboarding_token: null, phone: null, role: null, issued_at: null }),
    }),
    { name: 'pet-marketplace-console-session' },
  ),
);
